import { Price, Stack, Text } from "flicket-ui";
import PriceOrFree from "../../common/PriceOrFree";
import { PriceInfoProps } from "../types";
import { useOrganization } from "~hooks";
import { isChilliPeppersFlexiMembershipHack } from "~lib";
import { useAtom } from "jotai";
import {
  isPrechargeFlowAtom,
  presaleStartDateAtom,
} from "~features/nonseated-reservation/isPrechargeFlowAtom";
import { getDifferenceInDays } from "~features/single-page-checkout/helpers";

/**
 * Component to display price information based on current state
 */
export const PriceInfo = ({
  isExchangeFlow,
  ticketsLoading,
  hasSelection,
  showFees,
  cart,
  ownedTicketsExchangeValue,
  timezone,
}: PriceInfoProps) => {
  const { organization } = useOrganization();

  const [isPrechargeFlow] = useAtom(isPrechargeFlowAtom);
  const [startDate] = useAtom(presaleStartDateAtom);

  if (isExchangeFlow && ticketsLoading) {
    return null;
  }

  if (hasSelection) {
    if (isExchangeFlow) {
      const isLesserExchangeValue =
        cart.total < ownedTicketsExchangeValue &&
        !isChilliPeppersFlexiMembershipHack(organization.id);

      return (
        <>
          <Text variant="header.Minor">Difference to pay</Text>
          <Stack gap="1/2" direction="horizontal" alignItems="baseline">
            <Text variant="header.S">
              <PriceOrFree
                price={cart.total}
                currency={organization.currency}
              />
            </Text>
            <Text variant="small">Including fees</Text>
          </Stack>
          {isLesserExchangeValue && (
            <Text variant="small">
              You have selected a lower-priced value.
              <br />
              The difference won&apos;t be refunded.
            </Text>
          )}
        </>
      );
    }

    if (isPrechargeFlow && startDate && timezone) {
      const days = getDifferenceInDays(
        new Date(startDate),
        new Date(),
        timezone
      );
      return (
        <Text variant="header.S">
          Payment today{" "}
          <Price
            currency={organization.currency}
            price={days > 0 ? 0 : cart.total}
          />
        </Text>
      );
    }

    return (
      <>
        <Text variant="header.S">
          <PriceOrFree price={cart.total} currency={organization.currency} />
        </Text>
        {showFees && cart.total > 0 && (
          <Text variant="small">Including fees</Text>
        )}
      </>
    );
  }

  return null;
};
